/**
 * Reads pages of the A2Pay manual (work items 830 and 882) and turns them into DOM the backoffice can
 * show. The public user manual is fetched from the static copy shipped at /help/; the technical
 * manual comes from the Loans API. Both are plain HTML written by us, but they are still rebuilt
 * node by node from an allow-list rather than bound with innerHTML: Angular's sanitiser would strip
 * the ids every section link depends on, and bypassing it would trust markup wholesale.
 */

/** Where the public user manual is served in the backoffice build (angular.json copies it there). */
export const PUBLIC_MANUAL_BASE = "/help/";

/** The backoffice route of the technical manual. */
export const TECHNICAL_MANUAL_ROUTE = "/technical-manual";

/** One h2 section of a page: its heading and everything up to the next h2. */
export interface ManualSection {
  id: string;
  heading: string;
  nodes: readonly Node[];
  /** Every id inside the section, so a link to an h3 can find the section that holds it. */
  ids: ReadonlySet<string>;
}

/** A parsed manual page. The nodes belong to a detached document and are never moved, only copied. */
export interface ManualPage {
  file: string;
  title: string;
  /** "Describes the system as built on ...", from the page's data-built attribute. */
  built: string | null;
  /** What sits between the h1 and the first section: the lead paragraph, opening callouts. */
  intro: readonly Node[];
  sections: readonly ManualSection[];
}

/** Thrown when a response holds no manual page, as when the static host answers a missing path with the app itself. */
export class ManualPageMissingError extends Error {
  constructor(readonly file: string) {
    super(`${file} is not a page of the manual`);
  }
}

/** Where a link in the manual leads, decided once when the link is built. */
export type ManualLink =
  | { kind: "anchor"; section: string }
  | { kind: "public"; page: string; section: string | null; url: string }
  | { kind: "technical"; page: string; section: string | null; url: string }
  | { kind: "external"; url: string }
  | { kind: "none" };

/** The same slug manual.js and the link checker give an h2 that has no id. */
export function slugOf(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/** A manual page name: lower case, hyphens and .html, so a link can never name a path. */
export function isManualPageName(name: string): boolean {
  return /^[a-z0-9-]+\.html$/.test(name);
}

/** Splits a manual page into its title, intro and h2 sections. */
export function parseManualPage(file: string, html: string, parser: DOMParser = new DOMParser()): ManualPage {
  const doc = parser.parseFromString(html, "text/html");
  const main = doc.querySelector("main");
  if (!main) throw new ManualPageMissingError(file);

  const title = main.querySelector("h1")?.textContent?.trim() || file;
  const intro: Node[] = [];
  const sections: { id: string; heading: string; nodes: Node[] }[] = [];

  for (const node of Array.from(main.childNodes)) {
    if (node instanceof Element && node.tagName === "H1") continue;
    if (node instanceof Element && node.tagName === "H2") {
      const heading = node.textContent?.trim() ?? "";
      sections.push({ id: node.id || slugOf(heading), heading, nodes: [node] });
    } else if (sections.length) {
      sections[sections.length - 1].nodes.push(node);
    } else {
      intro.push(node);
    }
  }

  return {
    file,
    title,
    built: doc.documentElement.getAttribute("data-built"),
    intro,
    sections: sections.map((s) => ({ ...s, ids: idsWithin(s.id, s.nodes) })),
  };
}

function idsWithin(sectionId: string, nodes: Node[]): Set<string> {
  const ids = new Set([sectionId]);
  for (const node of nodes) {
    if (!(node instanceof Element)) continue;
    if (node.id) ids.add(node.id);
    node.querySelectorAll("[id]").forEach((el) => ids.add(el.id));
  }
  return ids;
}

/** The section with this id, or the one holding an element with it (an h3 anchor). */
export function findSection(page: ManualPage, id: string | null | undefined): ManualSection | null {
  if (!id) return null;
  return page.sections.find((s) => s.id === id) ?? page.sections.find((s) => s.ids.has(id)) ?? null;
}

/**
 * Decides where a link in the manual leads. Pages link to each other as "page.html#section"; a
 * tech-* page is the technical manual, anything else the public one. Schemes other than http(s)
 * and mailto, absolute and parent paths, and anything that is not a page name lead nowhere.
 */
export function resolveManualHref(href: string | null): ManualLink {
  // Browsers ignore tabs, newlines and other control characters inside a URL, so "java\nscript:"
  // would still run; strip them before deciding.
  const value = (href ?? "").replace(/[\u0000- \u007f]/g, "");
  if (!value) return { kind: "none" };
  if (value.startsWith("#")) return value.length > 1 ? { kind: "anchor", section: value.slice(1) } : { kind: "none" };

  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value)?.[1]?.toLowerCase();
  if (scheme) return ["http", "https", "mailto"].includes(scheme) ? { kind: "external", url: value } : { kind: "none" };

  const match = /^([a-z0-9-]+\.html)(?:#(.+))?$/.exec(value);
  if (!match) return { kind: "none" };
  const [, page, section = null] = match;
  const hash = section ? `#${section}` : "";
  return page.startsWith("tech-")
    ? { kind: "technical", page, section, url: `${TECHNICAL_MANUAL_ROUTE}/${page}${hash}` }
    : { kind: "public", page, section, url: `${PUBLIC_MANUAL_BASE}${page}${hash}` };
}

// Elements the manual is written with. Anything else is unwrapped (its text kept) or, if it could
// run, load or submit something, dropped with everything inside it.
const ALLOWED_TAGS = new Set([
  "A", "B", "BLOCKQUOTE", "BR", "CAPTION", "CODE", "DD", "DIV", "DL", "DT", "EM", "H1", "H2", "H3", "H4",
  "HR", "I", "KBD", "LI", "MARK", "OL", "P", "PRE", "SMALL", "SPAN", "STRONG", "SUB", "SUP", "TABLE",
  "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "UL",
]);
const DROPPED_TAGS = new Set([
  "SCRIPT", "STYLE", "IFRAME", "FRAME", "OBJECT", "EMBED", "TEMPLATE", "NOSCRIPT", "SVG", "MATH", "FORM",
  "INPUT", "BUTTON", "TEXTAREA", "SELECT", "LINK", "META", "BASE", "IMG", "VIDEO", "AUDIO", "SOURCE", "CANVAS",
]);
// href is handled on its own; event handlers and style never get through.
const ALLOWED_ATTRIBUTES = new Set(["id", "class", "colspan", "rowspan", "start", "title"]);

/**
 * Copies manual nodes into a fragment of `target`, keeping only allowed elements and attributes.
 * Links are rewritten to real backoffice addresses and tagged with where they lead, so a click can
 * be handled without parsing the address again; external links open in a new tab.
 */
export function buildSafeFragment(nodes: readonly Node[], target: Document): DocumentFragment {
  const fragment = target.createDocumentFragment();
  nodes.forEach((node) => appendSafe(node, fragment, target));
  return fragment;
}

function appendSafe(node: Node, parent: Node, target: Document): void {
  if (node.nodeType === Node.TEXT_NODE) {
    parent.appendChild(target.createTextNode(node.textContent ?? ""));
    return;
  }
  if (!(node instanceof Element) || DROPPED_TAGS.has(node.tagName)) return;

  if (!ALLOWED_TAGS.has(node.tagName)) {
    node.childNodes.forEach((child) => appendSafe(child, parent, target));
    return;
  }

  const copy = target.createElement(node.tagName.toLowerCase());
  for (const attribute of Array.from(node.attributes)) {
    if (ALLOWED_ATTRIBUTES.has(attribute.name)) copy.setAttribute(attribute.name, attribute.value);
  }
  if (node.tagName === "A") applyLink(copy, resolveManualHref(node.getAttribute("href")));
  node.childNodes.forEach((child) => appendSafe(child, copy, target));
  parent.appendChild(copy);
}

function applyLink(anchor: Element, link: ManualLink): void {
  anchor.setAttribute("data-manual-link", link.kind);
  switch (link.kind) {
    case "anchor":
      anchor.setAttribute("href", `#${link.section}`);
      anchor.setAttribute("data-manual-section", link.section);
      break;
    case "public":
    case "technical":
      anchor.setAttribute("href", link.url);
      anchor.setAttribute("data-manual-page", link.page);
      if (link.section) anchor.setAttribute("data-manual-section", link.section);
      break;
    case "external":
      anchor.setAttribute("href", link.url);
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
      break;
  }
}

/** Reads back where a built link leads, for the click handler. External and dead links return null. */
export function linkOf(anchor: Element): ManualLink | null {
  const kind = anchor.getAttribute("data-manual-link");
  const section = anchor.getAttribute("data-manual-section");
  const page = anchor.getAttribute("data-manual-page") ?? "";
  if (kind === "anchor" && section) return { kind, section };
  if (kind === "public" || kind === "technical") {
    return { kind, page, section, url: anchor.getAttribute("href") ?? "" };
  }
  return null;
}
